<template>
  <canvas ref="canvasRef" class="absolute inset-0 h-full w-full" aria-hidden="true"></canvas>
</template>

<script setup lang="ts">
let cleanup: (() => void) | undefined

const canvasRef = ref<HTMLCanvasElement | null>(null)

onMounted(async () => {
  const canvas = canvasRef.value
  if (!canvas) return

  const THREE = await import('three')

  const prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches

  const NODE_COUNT = 110
  const BOUNDS = { x: 22, y: 12, z: 6 }
  const LINK_DISTANCE = 4.2
  const GOLD = new THREE.Color('#DAA520')
  const GOLD_BRIGHT = new THREE.Color('#E6BB52')

  const renderer = new THREE.WebGLRenderer({ canvas, alpha: true, antialias: true })
  renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2))

  const scene = new THREE.Scene()
  const camera = new THREE.PerspectiveCamera(50, 1, 0.1, 100)
  camera.position.z = 16

  // Nodes: drifting golden points representing connected dispensers
  const positions = new Float32Array(NODE_COUNT * 3)
  const velocities = new Float32Array(NODE_COUNT * 3)
  for (let i = 0; i < NODE_COUNT; i++) {
    positions[i * 3] = (Math.random() * 2 - 1) * BOUNDS.x
    positions[i * 3 + 1] = (Math.random() * 2 - 1) * BOUNDS.y
    positions[i * 3 + 2] = (Math.random() * 2 - 1) * BOUNDS.z
    velocities[i * 3] = (Math.random() * 2 - 1) * 0.012
    velocities[i * 3 + 1] = (Math.random() * 2 - 1) * 0.008
    velocities[i * 3 + 2] = (Math.random() * 2 - 1) * 0.006
  }

  const nodeGeometry = new THREE.BufferGeometry()
  nodeGeometry.setAttribute('position', new THREE.BufferAttribute(positions, 3))
  const nodeMaterial = new THREE.PointsMaterial({
    color: GOLD_BRIGHT,
    size: 0.14,
    transparent: true,
    opacity: 0.85,
    sizeAttenuation: true,
    depthWrite: false,
    blending: THREE.AdditiveBlending,
  })
  const points = new THREE.Points(nodeGeometry, nodeMaterial)
  scene.add(points)

  // Links: faint golden lines between nearby nodes
  const maxLinks = NODE_COUNT * 6
  const linkPositions = new Float32Array(maxLinks * 2 * 3)
  const linkColors = new Float32Array(maxLinks * 2 * 3)
  const linkGeometry = new THREE.BufferGeometry()
  linkGeometry.setAttribute('position', new THREE.BufferAttribute(linkPositions, 3))
  linkGeometry.setAttribute('color', new THREE.BufferAttribute(linkColors, 3))
  const linkMaterial = new THREE.LineBasicMaterial({
    vertexColors: true,
    transparent: true,
    opacity: 0.35,
    depthWrite: false,
    blending: THREE.AdditiveBlending,
  })
  const links = new THREE.LineSegments(linkGeometry, linkMaterial)
  scene.add(links)

  const updateLinks = () => {
    let linkIndex = 0
    for (let i = 0; i < NODE_COUNT && linkIndex < maxLinks; i++) {
      for (let j = i + 1; j < NODE_COUNT && linkIndex < maxLinks; j++) {
        const dx = positions[i * 3]! - positions[j * 3]!
        const dy = positions[i * 3 + 1]! - positions[j * 3 + 1]!
        const dz = positions[i * 3 + 2]! - positions[j * 3 + 2]!
        const dist = Math.sqrt(dx * dx + dy * dy + dz * dz)
        if (dist < LINK_DISTANCE) {
          const strength = 1 - dist / LINK_DISTANCE
          const base = linkIndex * 6
          linkPositions[base] = positions[i * 3]!
          linkPositions[base + 1] = positions[i * 3 + 1]!
          linkPositions[base + 2] = positions[i * 3 + 2]!
          linkPositions[base + 3] = positions[j * 3]!
          linkPositions[base + 4] = positions[j * 3 + 1]!
          linkPositions[base + 5] = positions[j * 3 + 2]!
          for (let k = 0; k < 2; k++) {
            linkColors[base + k * 3] = GOLD.r * strength
            linkColors[base + k * 3 + 1] = GOLD.g * strength
            linkColors[base + k * 3 + 2] = GOLD.b * strength
          }
          linkIndex++
        }
      }
    }
    linkGeometry.setDrawRange(0, linkIndex * 2)
    linkGeometry.attributes.position!.needsUpdate = true
    linkGeometry.attributes.color!.needsUpdate = true
  }

  const advance = () => {
    for (let i = 0; i < NODE_COUNT; i++) {
      for (let axis = 0; axis < 3; axis++) {
        const idx = i * 3 + axis
        positions[idx]! += velocities[idx]!
        const bound = axis === 0 ? BOUNDS.x : axis === 1 ? BOUNDS.y : BOUNDS.z
        if (Math.abs(positions[idx]!) > bound) velocities[idx]! *= -1
      }
    }
    nodeGeometry.attributes.position!.needsUpdate = true
    updateLinks()
  }

  // Subtle parallax toward the pointer
  const pointer = { x: 0, y: 0 }
  const onPointerMove = (event: PointerEvent) => {
    pointer.x = (event.clientX / window.innerWidth) * 2 - 1
    pointer.y = (event.clientY / window.innerHeight) * 2 - 1
  }
  window.addEventListener('pointermove', onPointerMove, { passive: true })

  const resize = () => {
    const { clientWidth, clientHeight } = canvas
    if (clientWidth === 0 || clientHeight === 0) return
    renderer.setSize(clientWidth, clientHeight, false)
    camera.aspect = clientWidth / clientHeight
    camera.updateProjectionMatrix()
  }
  const resizeObserver = new ResizeObserver(() => {
    resize()
    if (prefersReducedMotion) renderer.render(scene, camera)
  })
  resizeObserver.observe(canvas)
  resize()

  let frameId = 0
  let visible = true

  const renderFrame = () => {
    advance()
    camera.position.x += (pointer.x * 1.2 - camera.position.x) * 0.03
    camera.position.y += (-pointer.y * 0.8 - camera.position.y) * 0.03
    camera.lookAt(0, 0, 0)
    renderer.render(scene, camera)
  }

  const loop = () => {
    frameId = requestAnimationFrame(loop)
    renderFrame()
  }

  const start = () => {
    if (!frameId && visible && !document.hidden && !prefersReducedMotion) loop()
  }
  const stop = () => {
    cancelAnimationFrame(frameId)
    frameId = 0
  }

  // Render a single static frame for reduced motion, otherwise animate
  if (prefersReducedMotion) {
    advance()
    renderFrame()
  } else {
    start()
  }

  const onVisibilityChange = () => (document.hidden ? stop() : start())
  document.addEventListener('visibilitychange', onVisibilityChange)

  const intersectionObserver = new IntersectionObserver(([entry]) => {
    visible = entry?.isIntersecting ?? true
    if (visible) {
      start()
    } else {
      stop()
    }
  })
  intersectionObserver.observe(canvas)

  cleanup = () => {
    stop()
    document.removeEventListener('visibilitychange', onVisibilityChange)
    window.removeEventListener('pointermove', onPointerMove)
    resizeObserver.disconnect()
    intersectionObserver.disconnect()
    nodeGeometry.dispose()
    nodeMaterial.dispose()
    linkGeometry.dispose()
    linkMaterial.dispose()
    renderer.dispose()
  }
})

onBeforeUnmount(() => cleanup?.())
</script>
